import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ListEditor } from "@/components/admin/ListEditor";
import { GcalPanel } from "@/components/admin/GcalPanel";
import { ShopNewsPanel } from "@/components/admin/ShopNewsPanel";
import { HeroImagePanel } from "@/components/admin/HeroImagePanel";
import { AboutImagePanel } from "@/components/admin/AboutImagePanel";
import { Trash2 } from "lucide-react";

function ConsultationTypesEditor({ items, onChange }) {
  const norm = (items || []).map((t) => typeof t === "string" ? { name: t, duration_min: 60, location: "" } : { name: t.name, duration_min: t.duration_min ?? 60, location: t.location || "" });
  const upd = (i, k, v) => { const n = norm.map((t, j) => j === i ? { ...t, [k]: v } : t); onChange(n); };
  return (
    <div className="space-y-3" data-testid="settings-consultation-types">
      {norm.map((t, i) => (
        <div key={i} className="border border-hair p-4 grid gap-3" data-testid={`settings-ctype-${i}`}>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome</Label>
              <Input value={t.name} onChange={(e) => upd(i, "name", e.target.value)} className="rounded-none mt-1" data-testid={`settings-ctype-name-${i}`} />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Durata (min)</Label>
              <Input type="number" min={15} step={15} value={t.duration_min} onChange={(e) => upd(i, "duration_min", Number(e.target.value))} className="rounded-none mt-1" data-testid={`settings-ctype-dur-${i}`} />
            </div>
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Modalità o luogo (mostrato al cliente)</Label>
              <Input value={t.location || ""} onChange={(e) => upd(i, "location", e.target.value)} className="rounded-none mt-1" placeholder="Es. Studio — Via ... oppure Video call online" data-testid={`settings-ctype-loc-${i}`} />
            </div>
            <button type="button" onClick={() => onChange(norm.filter((_, j) => j !== i))} className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone transition-colors" data-testid={`settings-ctype-del-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...norm, { name: "", duration_min: 60, location: "" }])}
        className="min-h-[44px] px-4 text-xs tracking-[0.14em] uppercase border border-ink hover:bg-ink hover:text-bone transition-colors" data-testid="settings-ctype-add">
        + Aggiungi tipologia
      </button>
      <p className="text-xs text-taupe">La consulenza è sempre gratuita: al cliente arriva subito l'e-mail di conferma con l'invito calendario.</p>
    </div>
  );
}

const WEEKDAYS = [
  { js: 1, label: "Lunedì" },
  { js: 2, label: "Martedì" },
  { js: 3, label: "Mercoledì" },
  { js: 4, label: "Giovedì" },
  { js: 5, label: "Venerdì" },
  { js: 6, label: "Sabato" },
  { js: 0, label: "Domenica" },
];

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="eyebrow !tracking-[0.14em]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export function SettingsPanel({ initial, onSave, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const toggleWeekday = (js) =>
    setF((p) => ({
      ...p,
      availability_weekdays_off: p.availability_weekdays_off.includes(js)
        ? p.availability_weekdays_off.filter((d) => d !== js)
        : [...p.availability_weekdays_off, js],
    }));

  const saveSection = (keys) => onSave(Object.fromEntries(keys.map((k) => [k, f[k]])));

  const SaveBtn = ({ keys, testId }) => (
    <button onClick={() => saveSection(keys)} disabled={busy} data-testid={testId}
      className="btn-ink mt-6 w-full sm:w-auto min-h-[44px] disabled:opacity-60">
      {busy ? "Salvataggio…" : "Salva"}
    </button>
  );

  return (
    <div className="space-y-10 max-w-3xl" data-testid="settings-panel">
      {/* Contatti */}
      <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-contacts">
        <div className="eyebrow">Contatti & Indirizzo</div>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <Field label="Telefono" testId="settings-field-phone">
            <Input value={f.phone} onChange={set("phone")} data-testid="settings-phone" />
          </Field>
          <Field label="Email" testId="settings-field-email">
            <Input value={f.email} onChange={set("email")} data-testid="settings-email" />
          </Field>
          <Field label="Indirizzo (riga 1)" testId="settings-field-addr1">
            <Input value={f.address_line1} onChange={set("address_line1")} data-testid="settings-address-line1" />
          </Field>
          <Field label="Indirizzo (riga 2)" testId="settings-field-addr2">
            <Input value={f.address_line2} onChange={set("address_line2")} data-testid="settings-address-line2" />
          </Field>
          <Field label="P.IVA" testId="settings-field-piva">
            <Input value={f.piva} onChange={set("piva")} data-testid="settings-piva" />
          </Field>
          <Field label="Orari di apertura" testId="settings-field-hours">
            <Input value={f.opening_hours} onChange={set("opening_hours")} data-testid="settings-opening-hours" />
          </Field>
        </div>
        <SaveBtn keys={["phone", "email", "address_line1", "address_line2", "piva", "opening_hours"]} testId="settings-save-contacts" />
      </section>

      {/* Social & WhatsApp */}
      <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-social">
        <div className="eyebrow">Social & WhatsApp</div>
        <div className="grid gap-6 mt-6">
          <Field label="URL Instagram" testId="settings-field-instagram">
            <Input value={f.instagram_url} onChange={set("instagram_url")} data-testid="settings-instagram" />
          </Field>
          <Field label="Numero WhatsApp (solo cifre, con prefisso)" testId="settings-field-whatsapp">
            <Input value={f.whatsapp_phone} onChange={set("whatsapp_phone")} data-testid="settings-whatsapp-phone" />
          </Field>
          <Field label="Messaggio precompilato WhatsApp" testId="settings-field-wamsg">
            <Textarea rows={3} value={f.whatsapp_message} onChange={set("whatsapp_message")} data-testid="settings-whatsapp-message" />
          </Field>
        </div>
        <SaveBtn keys={["instagram_url", "whatsapp_phone", "whatsapp_message"]} testId="settings-save-social" />
      </section>

      {/* Testi sito */}
      <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-texts">
        <div className="eyebrow">Testi del sito</div>
        <div className="grid gap-6 mt-6">
          <Field label="Aree servite" testId="settings-field-areas">
            <Input value={f.service_areas} onChange={set("service_areas")} data-testid="settings-service-areas" />
          </Field>
          <Field label="Durata consulenza (testo)" testId="settings-field-duration">
            <Input value={f.consultation_duration} onChange={set("consultation_duration")} data-testid="settings-consultation-duration" />
          </Field>
          <Field label="Testo breve nel footer" testId="settings-field-footer">
            <Textarea rows={2} value={f.footer_short} onChange={set("footer_short")} data-testid="settings-footer-short" />
          </Field>
        </div>
        <SaveBtn keys={["service_areas", "consultation_duration", "footer_short"]} testId="settings-save-texts" />
      </section>

      {/* Disponibilità calendario */}
      <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-availability">
        <div className="eyebrow">Disponibilità & Calendario</div>
        <div className="grid gap-8 mt-6">
          <Field label="Giorni di chiusura settimanale" testId="settings-field-weekdays-off">
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(({ js, label }) => {
                const off = f.availability_weekdays_off.includes(js);
                return (
                  <button key={js} type="button" onClick={() => toggleWeekday(js)} data-testid={`settings-weekday-${js}`}
                    className={`px-4 py-2 min-h-[44px] text-sm border transition-colors ${off ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-taupe mt-2">I giorni evidenziati non sono prenotabili.</p>
          </Field>
          <Field label="Slot orari prenotabili (formato HH:MM)" testId="settings-field-slots">
            <ListEditor items={f.availability_slots} onChange={set("availability_slots")} placeholder="es. 10:00" addLabel="Aggiungi" testId="settings-slots"
              validate={(v) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v)} />
          </Field>
          <Field label="Date bloccate / festività (YYYY-MM-DD)" testId="settings-field-blocked">
            <ListEditor items={f.blocked_dates} onChange={set("blocked_dates")} placeholder="es. 2026-08-15" addLabel="Blocca" testId="settings-blocked"
              validate={(v) => /^\d{4}-\d{2}-\d{2}$/.test(v)} />
          </Field>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Preavviso minimo (giorni)" testId="settings-field-min-notice">
              <Input type="number" min={0} value={f.min_notice_days} onChange={(e) => setF((p) => ({ ...p, min_notice_days: Number(e.target.value) }))} data-testid="settings-min-notice" />
            </Field>
            <Field label="Prenotabile fino a (giorni)" testId="settings-field-max-advance">
              <Input type="number" min={1} value={f.max_advance_days} onChange={(e) => setF((p) => ({ ...p, max_advance_days: Number(e.target.value) }))} data-testid="settings-max-advance" />
            </Field>
          </div>
          <Field label="Tipologie di consulenza (nome, durata, luogo)" testId="settings-field-ctypes">
            <ConsultationTypesEditor items={f.consultation_types} onChange={set("consultation_types")} />
          </Field>
        </div>
        <SaveBtn keys={["availability_slots", "availability_weekdays_off", "blocked_dates", "min_notice_days", "max_advance_days", "consultation_types"]} testId="settings-save-availability" />
      </section>

      <GcalPanel />
      <ShopNewsPanel />
      <AboutImagePanel />

      <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-shop">
        <div className="eyebrow">Shop</div>
        <label className="mt-4 flex items-center gap-3 text-sm cursor-pointer min-h-[44px] max-w-md">
          <Switch checked={f.shop_visible !== false} onCheckedChange={(v) => { setF((p) => ({ ...p, shop_visible: v })); onSave({ shop_visible: v }); }} data-testid="settings-shop-visible" />
          <span>Shop visibile sul sito<span className="block text-xs text-taupe">Effetto immediato al click: la voce sparisce dal menu e /shop non è più accessibile. I prodotti non vengono eliminati.</span></span>
        </label>
        <label className="mt-3 flex items-center gap-3 text-sm cursor-pointer min-h-[44px] max-w-md">
          <Switch checked={f.library_visible !== false} onCheckedChange={(v) => { setF((p) => ({ ...p, library_visible: v })); onSave({ library_visible: v }); }} data-testid="settings-library-visible" />
          <span>Library (Guide gratuite) visibile sul sito<span className="block text-xs text-taupe">Effetto immediato al click: la voce sparisce dal menu e /library non è più accessibile. Le guide restano in dashboard.</span></span>
        </label>
        <SaveBtn keys={["shop_visible", "library_visible"]} testId="settings-save-shop" />
      </section>

      {/* Shop the Project / Vantaggio Clienti Studio */}
      <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-studio">
        <div className="eyebrow">Shop the Project · Vantaggio Clienti Studio</div>
        <p className="text-xs text-taupe mt-3 leading-relaxed max-w-xl">I clienti con link personale vedono automaticamente il prezzo riservato (listino − percentuale) sui prodotti idonei. Nessun coupon pubblico: il vantaggio è validato dal server a ogni ordine. Puoi escludere singoli prodotti dalla scheda prodotto, oppure intere categorie/brand qui sotto.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <Field label="Percentuale vantaggio (%)" testId="settings-field-studio-percent">
            <Input type="number" min={0} max={90} value={f.studio_discount_percent ?? 10} onChange={set("studio_discount_percent")} data-testid="settings-studio-percent" />
          </Field>
          <Field label="Validità link cliente (giorni)" testId="settings-field-studio-days">
            <Input type="number" min={1} value={f.studio_token_days ?? 180} onChange={set("studio_token_days")} data-testid="settings-studio-days" />
          </Field>
          <Field label="Categorie escluse (separate da virgola)" testId="settings-field-studio-exc-cat">
            <Input value={Array.isArray(f.studio_excluded_categories) ? f.studio_excluded_categories.join(", ") : (f.studio_excluded_categories || "")} onChange={(e) => setF((p) => ({ ...p, studio_excluded_categories: e.target.value }))} data-testid="settings-studio-excluded-categories" />
          </Field>
          <Field label="Brand esclusi (separati da virgola)" testId="settings-field-studio-exc-brand">
            <Input value={Array.isArray(f.studio_excluded_brands) ? f.studio_excluded_brands.join(", ") : (f.studio_excluded_brands || "")} onChange={(e) => setF((p) => ({ ...p, studio_excluded_brands: e.target.value }))} data-testid="settings-studio-excluded-brands" />
          </Field>
        </div>
        <div className="eyebrow mt-8 mb-2">Tariffe servizio Shop the Project</div>
        <p className="text-xs text-taupe mb-4">Il prezzo è determinato dalla superficie; oltre 110 m² il cliente richiede una quotazione (nessun pagamento online).</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["stp_price_25", "Fino a 25 m² (€)"], ["stp_price_50", "Fino a 50 m² (€)"], ["stp_price_70", "Fino a 70 m² (€)"], ["stp_price_110", "Fino a 110 m² (€)"]].map(([k, l]) => (
            <Field key={k} label={l} testId={`settings-field-${k}`}>
              <Input type="number" min={1} value={f[k] ?? ""} onChange={set(k)} data-testid={`settings-${k}`} />
            </Field>
          ))}
        </div>
        <button
          onClick={() => {
            const norm = {
              studio_excluded_categories: typeof f.studio_excluded_categories === "string" ? f.studio_excluded_categories.split(",").map((x) => x.trim()).filter(Boolean) : (f.studio_excluded_categories || []),
              studio_excluded_brands: typeof f.studio_excluded_brands === "string" ? f.studio_excluded_brands.split(",").map((x) => x.trim()).filter(Boolean) : (f.studio_excluded_brands || []),
            };
            onSave({
              studio_discount_percent: Number(f.studio_discount_percent ?? 10),
              studio_token_days: Number(f.studio_token_days ?? 180),
              stp_price_25: Number(f.stp_price_25 ?? 149),
              stp_price_50: Number(f.stp_price_50 ?? 249),
              stp_price_70: Number(f.stp_price_70 ?? 349),
              stp_price_110: Number(f.stp_price_110 ?? 449),
              ...norm,
            });
          }}
          disabled={busy} data-testid="settings-save-studio"
          className="btn-ink mt-6 w-full sm:w-auto min-h-[44px] disabled:opacity-60">
          {busy ? "Salvataggio…" : "Salva"}
        </button>
      </section>

      <HeroImagePanel onSaved={undefined} />
    </div>
  );
}
